import { createRouter, createWebHistory } from "vue-router";

import { IconUserSquare } from "@lde/core_lde_vue";
import { UilSearchAlt, UilPresentationPlay } from "@lde/vue-unicons";

import store from "@/store";
import config from "@/config";
import i18n from "@/modules/i18n";
import routes from "@/router/routes";
import { isOnPublicDomain, hasSeparatePublicDomain } from "@/modules/utils";
import { PAYS_CATALOGUE_PUBLIC } from "@/modules/cataloguePublic";
import IconPanier from "@/components/icons/IconPanier.vue";

function getTitle(key, count) {
  return () => i18n.global.t(key, count);
}

function getUrls(trad) {
  // Pour chaque locale, prendre la traduction (inutile pour :id)
  if (trad === ":id") {
    return { path: `/${trad}` };
  }

  const aliases = Object.keys(i18n.global.messages.value).map((local) => `/${i18n.global.t(trad, local)}`);
  return { path: `/${i18n.global.t(trad)}`, alias: aliases };
}

function getNotFoundLocation(to) {
  return {
    name: "404",
    params: {
      pathMatch: to.path.replace(/^\//, "").split("/").filter(Boolean),
    },
    query: to.query,
    hash: to.hash,
  };
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    ...routes,
    {
      path: "/",
      name: "home",
      component: () => import("@/views/Home.vue"),
      meta: {
        cataloguePublic: true,
        accessNotLogged: true,
      },
    },
    {
      path: "/crm",
      name: "formulaire_crm",
      beforeEnter: () => {
        window.location.href = `${import.meta.env.BASE_URL}CRM.html`;
        return false;
      },
      meta: {
        cataloguePublic: true,
        accessNotLogged: true,
      },
    },
    {
      path: "/search",
      name: "search",
      component: () => import("@/views/ResultatsRecherche.vue"),
      meta: {
        title: getTitle("recherche.resultats-de-recherche"),
        icon: UilSearchAlt,
        cataloguePublic: true,
        accessNotLogged: true,
      },
    },
    {
      ...getUrls("url.compte"),
      name: "compte",
      component: () => import("@/views/Compte.vue"),
      meta: {
        title: getTitle("menu.votre-profil"),
        icon: IconUserSquare,
      },
    },
    {
      ...getUrls("url.tutoriels"),
      name: "tutoriels",
      component: () => import("@/views/Tutoriels.vue"),
      meta: {
        title: getTitle("footer.tutoriels"),
        icon: UilPresentationPlay,
        accessNotLogged: true,
      },
    },
    {
      ...getUrls("url.panier"),
      name: "panier",
      component: () => import("@/views/Panier.vue"),
      meta: {
        title: getTitle("panier.mon-panier"),
        icon: IconPanier,
        accessNotLogged: true,
        cataloguePublic: true,
      },
    },
    {
      path: "/demande-devis/:identifiant",
      name: "demande_devis",
      component: () => import("@/views/DemandeDevisVisiteur.vue"),
      meta: {
        title: getTitle("demande-devis.titre"),
        accessNotLogged: true,
        cataloguePublic: true,
        guestOnly: true,
      },
    },
    {
      path: "/403",
      name: "403",
      component: () => import("@/views/Forbidden.vue"),
    },
    {
      path: "/acces-refuse",
      name: "acces_refuse",
      component: () => import("@/views/AccesRefuse.vue"),
      meta: {
        // Page autonome : accessible sans être connecté et affichée sans menu ni header
        accessNotLogged: true,
        standalone: true,
      },
    },
    {
      path: "/session-retablie",
      name: "session_retablie",
      component: () => import("@/views/SessionRetablie.vue"),
      meta: {
        // Page d'arrivée de la reconnexion ouverte dans un nouvel onglet : sans menu ni vérification de session
        accessNotLogged: true,
        standalone: true,
      },
    },
    {
      path: "/:pathMatch(.*)*",
      name: "404",
      component: () => import("@/views/NotFound.vue"),
    },
    {
      path: "/demo_normandie",
      name: "demo_normandie",
      meta: {
        title: getTitle("Tutoriels Région Normandie"),
      },
      component: () => import("@/views/DemoNormandie.vue"),
    },
    {
      path: "/suivi-demandes-licences",
      name: "suivi_demandes_licences",
      component: () => import("@/views/SuiviDemandesLicences.vue"),
      meta: {
        title: getTitle("suivi-licences.suivi-des-demandes-de-licences"),
      },
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  // À chaque changement de route, on vide la sélection des lignes cochées
  if (to.name !== from.name) {
    store.dispatch("TableClassic/clearCheckedRows");
  }

  // 1. Page autonome d'accès refusé
  if (to.name === "acces_refuse") {
    if (!store.getters.isLogged) {
      await store.dispatch("login", { noRedirect: true });
    }
    return store.getters.isLogged ? next({ name: "home" }) : next();
  }

  // L'onglet de reconnexion n'a rien à charger : il prévient l'onglet d'origine puis se ferme
  if (to.name === "session_retablie") {
    return next();
  }

  // 2. Vérification initiale de session au chargement de l'application
  const accessNotLogged = Boolean(to.meta?.accessNotLogged);
  if (!store.getters.isLogged && !store.getters.isInitialCheckDone) {
    await store.dispatch("login", { noRedirect: accessNotLogged });
  }

  // Catalogue public : le pays peut être choisi via un lien direct (ex. /?pays=suisse depuis le site LDE)
  const paysDemande = typeof to.query?.pays === "string" ? to.query.pays.toLowerCase() : null;
  if (
    !store.getters.isLogged
    && store.getters.isCataloguePublicActif
    && PAYS_CATALOGUE_PUBLIC.includes(paysDemande)
    && paysDemande !== store.getters.cataloguePublicPays
  ) {
    try {
      await store.dispatch("setPaysCataloguePublic", paysDemande);
    } catch (e) {
      console.error("Impossible d'enregistrer le pays du catalogue public :", e);
    }
  }

  const isLogged = store.getters.isLogged;
  const isPublicDomain = isOnPublicDomain();
  const separateDomains = hasSeparatePublicDomain();
  const isCataloguePublicActif = store.getters.isCataloguePublicActif;
  // Une route marquée cataloguePublic n'est ouverte aux visiteurs que si le catalogue public
  // est actif : sinon elle redevient privée et déclenche la redirection vers le login
  const isPublicRoute = accessNotLogged && (!to.meta?.cataloguePublic || isCataloguePublicActif);

  // 3. Gestion multi-domaines (si un domaine public dédié est séparé du domaine connecté)
  if (separateDomains) {
    // 3a. Connecté sur le domaine public : on laisse passer pour afficher la modale d'avertissement dans App.vue
    if (isLogged && isPublicDomain) {
      return next();
    }
    // 3b. Non connecté sur le domaine connecté :
    // redirection CAS (sauf demande_devis -> redirection vers domaine public)
    if (!isLogged && !isPublicDomain) {
      if (to.name === "demande_devis" && config.url.cataloguePublicUrl) {
        try {
          const redirectTarget = new URL(to.fullPath, config.url.cataloguePublicUrl).href;
          window.location.href = redirectTarget;
          return next(false);
        } catch (e) {
          console.error("Invalid redirection URL for demande_devis:", e);
        }
      }
      store.commit("setIsRedirecting", true);
      window.location.href = `${config.loginUrl}?next=${encodeURIComponent(window.location.href)}`;
      return next(false);
    }
    // 3c. Non connecté sur le domaine public : visite libre sur les routes publiques
  }

  // 4. Protection des routes privées (en mono-domaine ou multi-domaine si route privée demandée sans session)
  if (!isLogged && !isPublicRoute) {
    store.commit("setIsRedirecting", true);
    window.location.href = `${config.loginUrl}?next=${encodeURIComponent(window.location.href)}`;
    return next(false);
  }

  // 5. Gestion des routes publiques (accessNotLogged: true)
  if (isPublicRoute) {
    if (to.meta?.guestOnly && isLogged && !store.getters.isOperateur) {
      return next(getNotFoundLocation(to));
    }

    // Si page d'accueil et visiteur non connecté avec catalogue public actif -> redirection vers le catalogue
    if (!isLogged && to.name === "home" && isCataloguePublicActif) {
      const slug = store.getters.cataloguePublicSlug;
      if (slug) {
        return next({
          name: "catalogues_ressources_numeriques",
          params: { catalogueSlug: slug },
        });
      }
    }
    return next();
  }

  if (to.meta?.permissions?.length > 0 && !store.getters.hasPerms(to.meta.permissions)) {
    return next({ name: "403" });
  }

  if (to.name === "demo_normandie" && store.getters.isNormandie !== true) {
    // On renvoie vers une 404, pas besoin d'indiquer que cette page existe si elle n'est pas accessible
    return next(getNotFoundLocation(to));
  }

  if (to.name === "suivi_demandes_licences" && store.getters.user?.role !== "Opérateur LDE") {
    return next(getNotFoundLocation(to));
  }

  return next();
});

export default router;
export { getUrls };
